import clamp from 'clamp';
import type React from 'react';

import type { TableGridView } from '@gitbook/doc-core';

import type { TableRecordKV } from '../../types';
import type { ColumnWidthsMap } from '../../utils/contexts';

/**
 * Adapted from /ContentEditor/common/layout/table.ts
 */
export const DEFAULT_TABLE_COLUMN_WIDTH = 128;
export const MIN_COLUMN_WIDTH = 40;
export const MAX_COLUMN_WIDTH = 800;
/**
 * Compute the size of each column.
 * - if a column has a defined width, it will use that (clamped to the min/max).
 * - otherwise, autosize columns are given an equal share of the remaining width.
 */
export function computeColumnWidths(
    view: TableGridView,
    editorWidth: number
): { widths: ColumnWidthsMap; totalWidth: number } {
    if (!view.useNewSizing) {
        return computeScaledColumnWidths(view, editorWidth);
    }

    const widths: ColumnWidthsMap = new Map();
    let totalWidth = 0;

    // Sum fixed widths from columns with defined widths.
    let reservedWidth = 0;
    const fixedColumnsCount = Object.keys(view.columnWidths || {}).length;
    view.columns.forEach((key) => {
        reservedWidth += view.columnWidths?.[key] || 0;
    });

    // How much width is left after the fixed-size columns have been allocated.
    const remainingWidth = Math.max(editorWidth - reservedWidth, 0);

    // Next, compute the width for the remaining autosized columns.
    const autosizedColumns = view.columns.length - fixedColumnsCount;
    const autoColumnWidth =
        autosizedColumns > 0 ? Math.floor(remainingWidth / autosizedColumns) : 0;
    const remainder =
        autosizedColumns > 0 ? remainingWidth - autoColumnWidth * autosizedColumns : 0;

    // The last column gets the remainder to handle anti-aliasing.
    const lastColumnId = view.columns[view.columns.length - 1];

    for (const colId of view.columns) {
        let finalWidth: number;
        if (view.columnWidths && typeof view.columnWidths[colId] !== 'undefined') {
            // Fixed column: use provided width with clamping at MIN_COLUMN_WIDTH.
            finalWidth = clamp(view.columnWidths[colId], MIN_COLUMN_WIDTH, MAX_COLUMN_WIDTH);
        } else {
            // Autosized column: use equal share of remaining width and assign remainder to the last column.
            finalWidth = autoColumnWidth;
            if (colId === lastColumnId) {
                finalWidth += remainder;
            }
            finalWidth = clamp(finalWidth, DEFAULT_TABLE_COLUMN_WIDTH, MAX_COLUMN_WIDTH);
        }
        widths.set(colId, finalWidth);
        totalWidth += finalWidth;
    }

    return { widths, totalWidth };
}

/**
 * Legacy scaled sizing logic.
 */
function computeScaledColumnWidths(
    view: TableGridView,
    editorWidth: number
): { widths: ColumnWidthsMap; totalWidth: number } {
    // When using scaled columns, minimum width is 150 and default 200.
    const MIN_WIDTH = 150;
    const DEFAULT_WIDTH = 200;
    const widths: ColumnWidthsMap = new Map();
    let totalWidth = 0;

    for (const colId of view.columns) {
        const w = view.columnWidths?.[colId] ?? DEFAULT_WIDTH;
        widths.set(colId, w);
        totalWidth += w;
    }

    // If the total width is less than the editor width, scale all columns up proportionally.
    if (totalWidth < editorWidth) {
        const scale = editorWidth / totalWidth;
        totalWidth = editorWidth;
        view.columns.forEach((colId) => {
            const oldWidth = widths.get(colId) ?? DEFAULT_WIDTH;
            const newWidth = clamp(oldWidth * scale, MIN_WIDTH, MAX_COLUMN_WIDTH);
            widths.set(colId, newWidth);
        });
    }

    return { widths, totalWidth };
}

// Function to compute total column height:
export function computeColumnHeights(
    view: TableGridView,
    records: TableRecordKV[],
    rowHeightsRef: React.MutableRefObject<Record<string, number>>,
    columnHeightsRef: React.MutableRefObject<Record<string, number>>
) {
    const heightsByColumnId: Record<string, number> = {};

    // For each column in view.columns
    for (const colId of view.columns) {
        // sum up the heights of all rows (since each row is the same height across columns)
        let totalHeight = 0;
        for (const record of records) {
            const rowHeight = rowHeightsRef.current[record.key] || 0;
            totalHeight += rowHeight;
        }
        heightsByColumnId[colId] = totalHeight;
    }
    columnHeightsRef.current = heightsByColumnId;
}
